import {
  ApiCallMethod,
  ApiRouteAuthConfig,
  ApiRouteVisibility,
  type ApiParamsInput,
  type ApiQueryInput,
  type FunctionizeConfig,
} from './types.js';
import type { UUID } from '@cabal/shared/domain/_common/types.js';
import type { CancelSubscriptionResponse, ChangeSubscriptionPlanRequest, CreateSubscriptionCheckoutRequest, CreateSubscriptionCheckoutResponse, CreateSubscriptionPortalResponse, GetCurrentSubscriptionResponse, GetSubscriptionPlansResponse, GetSubscriptionUsageResponse, ResumeSubscriptionResponse } from '@cabal/shared/domain/billing/subscription/types.js';
import type { ModelDetailResponse, ModelsListResponse } from '@cabal/shared/domain/llm/model/types.js';
import type { CollabsQueryParams, CollabsResponse } from '@cabal/shared/domain/session/collab/types.js';
import type { GeoContentRef, GeoResolveResponse } from '@cabal/shared/domain/geo/types.js';
import type { ClipFutureRequest, ClipFutureResponse, RenameRoundRequest, RenameRoundResponse, RenameSessionPathRequest, RenameSessionPathResponse, SessionPathsResponse, TravelSessionPathRequest, TravelSessionPathResponse } from '@cabal/shared/domain/session/path/types.js';
import type { ProductDetailResponse, ProductListResponse, ProductTimelineResponse, ProductsQueryParams } from '@cabal/shared/domain/session/product/types.js';
import type { AddTeamMemberResponse, CreateTeamMemberRequest, MemberListResponse, UpdateMemberRequest, UpdateTeamMemberResponse } from '@cabal/shared/domain/session/team/member/types.js';
import type { TagsListResponse } from '@cabal/shared/domain/session/team/tag/types.js';
import type { CreateTeamFromSessionRequest, CreateTeamRequest, CreateTeamResponse, ReplicateTeamResponse, SetTeamModelModeRequest, Team, TeamDetail, TeamsListQueryParams, TeamsListResponse, TeamsPreviewListResponse, UpdateTeamFullResponse, UpdateTeamRequest } from '@cabal/shared/domain/session/team/types.js';
import type { CreateSessionRequest, CreateSessionResponse, MissionBriefingResponse, Session, SessionInitialLoadQueryParams, SessionInitialLoadResponse, SessionIntentRequest, SessionIntentResponse, SessionOverviewResponse, SessionPreviewsResponse, SessionProductReadsQueryParams, SessionProductReadsResponse, SessionResourcesSummaryQueryParams, SessionResourcesSummaryResponse, SessionUpdateRequest, SessionUpdateResponse, SessionsCountQueryParams, SessionsCountResponse, SessionsListQueryParams, SessionsListResponse } from '@cabal/shared/domain/session/types.js';
import type { VersionDetailResponse, VersionListResponse, VersionsQueryParams } from '@cabal/shared/domain/session/version/types.js';
import type { HealthResponse, ReachOutRequest, ReachOutResponse } from '@cabal/shared/domain/system/types.js';
import type { AttachedFileSignedUrlRequest, AttachedFileSignedUrlResponse, TempUploadResponse, TempUploadSignedUrlRequest, TempUploadSignedUrlResponse } from '@cabal/shared/domain/upload/types.js';
import type { ProfileImageDeleteResponse, ProfileImageUploadResponse, UserProfileResponse, UserProfileUpdateRequest, UserPublicProfileResponse } from '@cabal/shared/domain/user/types.js';

export interface RouteContract<
  P extends ApiParamsInput | undefined,
  Q extends ApiQueryInput | undefined,
  B,
  R,
> extends FunctionizeConfig<P, Q, B> {
  readonly _response?: R;
}

function defineRoute<
  P extends ApiParamsInput | undefined,
  Q extends ApiQueryInput | undefined,
  B,
  R,
>(input: FunctionizeConfig<P, Q, B>): RouteContract<P, Q, B, R> {
  return input;
}

const PUBLIC_ROUTE_AUTH: ApiRouteAuthConfig = {
  visibility: ApiRouteVisibility.Public,
};

/**
 * Only `SERVER_ONLY_ROUTES` states this explicitly. Inside `ROUTE_CONTRACTS`,
 * private is the default an omitted `auth` already means.
 */
const PRIVATE_ROUTE_AUTH: ApiRouteAuthConfig = {
  visibility: ApiRouteVisibility.Private,
};

export interface SessionRouteParams {
  sessionId: UUID;
}

export interface SessionProductRouteParams extends SessionRouteParams {
  productId: UUID;
}

export interface SessionPathRouteParams extends SessionRouteParams {
  pathId: UUID;
}

export interface RenameRoundRouteParams extends SessionRouteParams {
  round: number;
}

export interface SessionProductVersionRouteParams extends SessionProductRouteParams {
  versionId: UUID;
}

export interface SessionTempUploadRouteParams extends SessionRouteParams {
  uploadId: UUID;
}

export interface SessionAttachedFileRouteParams extends SessionRouteParams {
  attachedFileId: UUID;
}

export interface TeamRouteParams {
  teamId: UUID;
}

export interface TeamMemberRouteParams extends TeamRouteParams {
  memberId: UUID;
}

export interface ModelRouteParams {
  id: number;
}

export interface UserRouteParams {
  userId: UUID;
}

enum SessionUploadRouteTemplate {
  Create = '/api/sessions/:sessionId/temp-uploads',
  Delete = '/api/sessions/:sessionId/temp-uploads/:uploadId',
  SignedUrl = '/api/sessions/:sessionId/temp-uploads/:uploadId/signed-url',
  AttachedFileSignedUrl = '/api/sessions/:sessionId/attached-files/:attachedFileId/signed-url',
}

export const ROUTE_CONTRACTS = {
  health: {
    get: defineRoute<undefined, undefined, undefined, HealthResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/health/',
      auth: PUBLIC_ROUTE_AUTH,
    }),
  },
  sessions: {
    list: defineRoute<undefined, SessionsListQueryParams, undefined, SessionsListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions',
    }),
    count: defineRoute<undefined, SessionsCountQueryParams, undefined, SessionsCountResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/count',
    }),
    resourcesSummary: defineRoute<undefined, SessionResourcesSummaryQueryParams, undefined, SessionResourcesSummaryResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/resources-summary',
    }),
    create: defineRoute<undefined, undefined, CreateSessionRequest, CreateSessionResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/create',
    }),
    get: defineRoute<SessionRouteParams, undefined, undefined, Session>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId',
    }),
    initialLoad: defineRoute<SessionRouteParams, SessionInitialLoadQueryParams, undefined, SessionInitialLoadResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/initial-load',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    previews: defineRoute<SessionRouteParams, undefined, undefined, SessionPreviewsResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/previews',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    overview: defineRoute<SessionRouteParams, undefined, undefined, SessionOverviewResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/overview',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    productReads: defineRoute<SessionRouteParams, SessionProductReadsQueryParams, undefined, SessionProductReadsResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/product-reads',
    }),
    team: defineRoute<SessionRouteParams, undefined, undefined, Team>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/team',
    }),
    missionBriefing: defineRoute<SessionRouteParams, undefined, undefined, MissionBriefingResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/mission-briefing',
    }),
    teamMembers: defineRoute<SessionRouteParams, undefined, undefined, MemberListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/team/members',
    }),
    update: defineRoute<SessionRouteParams, undefined, SessionUpdateRequest, SessionUpdateResponse>({
      method: ApiCallMethod.Patch,
      routeTemplate: '/api/sessions/:sessionId',
    }),
    remove: defineRoute<SessionRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Delete,
      routeTemplate: '/api/sessions/:sessionId',
    }),
    intent: defineRoute<SessionRouteParams, undefined, SessionIntentRequest, SessionIntentResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/intent',
    }),
    tempUploadCreate: defineRoute<SessionRouteParams, undefined, FormData, TempUploadResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: SessionUploadRouteTemplate.Create,
    }),
    tempUploadDelete: defineRoute<SessionTempUploadRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Delete,
      routeTemplate: SessionUploadRouteTemplate.Delete,
    }),
    tempUploadSignedUrl: defineRoute<SessionTempUploadRouteParams, undefined, TempUploadSignedUrlRequest, TempUploadSignedUrlResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: SessionUploadRouteTemplate.SignedUrl,
    }),
    attachedFileSignedUrl: defineRoute<SessionAttachedFileRouteParams, undefined, AttachedFileSignedUrlRequest, AttachedFileSignedUrlResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: SessionUploadRouteTemplate.AttachedFileSignedUrl,
    }),
    collabs: defineRoute<SessionRouteParams, CollabsQueryParams, undefined, CollabsResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/collabs',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    markChatRead: defineRoute<SessionRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/collabs/mark-read',
    }),
    products: defineRoute<SessionRouteParams, ProductsQueryParams, undefined, ProductListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/products',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    product: defineRoute<SessionProductRouteParams, undefined, undefined, ProductDetailResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/products/:productId',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    versions: defineRoute<SessionProductRouteParams, VersionsQueryParams, undefined, VersionListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/products/:productId/versions',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    version: defineRoute<SessionProductVersionRouteParams, undefined, undefined, VersionDetailResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/products/:productId/versions/:versionId',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    markRead: defineRoute<SessionProductRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/products/:productId/mark-read',
    }),
    timeline: defineRoute<SessionProductRouteParams, undefined, undefined, ProductTimelineResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/products/:productId/timeline',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    paths: defineRoute<SessionRouteParams, undefined, undefined, SessionPathsResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/sessions/:sessionId/paths',
    }),
    travel: defineRoute<SessionRouteParams, undefined, TravelSessionPathRequest, TravelSessionPathResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/travel',
    }),
    clipFuture: defineRoute<SessionRouteParams, undefined, ClipFutureRequest, ClipFutureResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/clip-future',
    }),
    renamePath: defineRoute<SessionPathRouteParams, undefined, RenameSessionPathRequest, RenameSessionPathResponse>({
      method: ApiCallMethod.Patch,
      routeTemplate: '/api/sessions/:sessionId/paths/:pathId',
    }),
    renameRound: defineRoute<RenameRoundRouteParams, undefined, RenameRoundRequest, RenameRoundResponse>({
      method: ApiCallMethod.Patch,
      routeTemplate: '/api/sessions/:sessionId/rounds/:round',
    }),
    halt: defineRoute<SessionRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/sessions/:sessionId/halt',
    }),
  },
  teams: {
    list: defineRoute<undefined, TeamsListQueryParams, undefined, TeamsListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/teams',
    }),
    listPreview: defineRoute<undefined, TeamsListQueryParams, undefined, TeamsPreviewListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/teams/preview',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    get: defineRoute<TeamRouteParams, undefined, undefined, TeamDetail>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/teams/:teamId',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    create: defineRoute<undefined, undefined, CreateTeamRequest, CreateTeamResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/teams',
    }),
    createFromSession: defineRoute<undefined, undefined, CreateTeamFromSessionRequest, CreateTeamResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/teams/from-session',
    }),
    replicate: defineRoute<TeamRouteParams, undefined, undefined, ReplicateTeamResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/teams/:teamId/replicate',
    }),
    update: defineRoute<TeamRouteParams, undefined, UpdateTeamRequest, UpdateTeamFullResponse>({
      method: ApiCallMethod.Put,
      routeTemplate: '/api/teams/:teamId',
    }),
    setModelMode: defineRoute<TeamRouteParams, undefined, SetTeamModelModeRequest, TeamDetail>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/teams/:teamId/model-mode',
    }),
    remove: defineRoute<TeamRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Delete,
      routeTemplate: '/api/teams/:teamId',
    }),
    addMember: defineRoute<TeamRouteParams, undefined, CreateTeamMemberRequest, AddTeamMemberResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/teams/:teamId/members',
    }),
    updateMember: defineRoute<TeamMemberRouteParams, undefined, UpdateMemberRequest, UpdateTeamMemberResponse>({
      method: ApiCallMethod.Patch,
      routeTemplate: '/api/teams/:teamId/members/:memberId',
    }),
    removeMember: defineRoute<TeamMemberRouteParams, undefined, undefined, void>({
      method: ApiCallMethod.Delete,
      routeTemplate: '/api/teams/:teamId/members/:memberId',
    }),
  },
  tags: {
    list: defineRoute<undefined, undefined, undefined, TagsListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/tags',
      auth: PUBLIC_ROUTE_AUTH,
    }),
  },
  users: {
    readPublic: defineRoute<UserRouteParams, undefined, undefined, UserPublicProfileResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/users/:userId/public',
    }),
  },
  profile: {
    get: defineRoute<undefined, undefined, undefined, UserProfileResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/users/profile',
    }),
    update: defineRoute<undefined, undefined, UserProfileUpdateRequest, UserProfileResponse>({
      method: ApiCallMethod.Patch,
      routeTemplate: '/api/users/profile',
    }),
    uploadImage: defineRoute<undefined, undefined, FormData, ProfileImageUploadResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/users/profile/image',
    }),
    deleteImage: defineRoute<undefined, undefined, undefined, ProfileImageDeleteResponse>({
      method: ApiCallMethod.Delete,
      routeTemplate: '/api/users/profile/image',
    }),
  },
  geo: {
    /**
     * Public because the content it reads is public — product/version and
     * collab reads are open, so an anonymous reader must be able to see a map.
     *
     * Public is safe ONLY because the body is a content reference. Geocoding is
     * a deployment-wide 1-request-per-second budget whose overuse bans our
     * egress IP for everyone; a public route taking `places: string[]` would be
     * an open proxy against it. Widening this body toward place text
     * reintroduces exactly that hole.
     */
    resolve: defineRoute<undefined, undefined, GeoContentRef, GeoResolveResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/geo/resolve',
      auth: PUBLIC_ROUTE_AUTH,
    }),
  },
  models: {
    list: defineRoute<undefined, undefined, undefined, ModelsListResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/models',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    get: defineRoute<ModelRouteParams, undefined, undefined, ModelDetailResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/models/:id',
      auth: PUBLIC_ROUTE_AUTH,
    }),
  },
  subscription: {
    plans: defineRoute<undefined, undefined, undefined, GetSubscriptionPlansResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/subscription/plans',
      auth: PUBLIC_ROUTE_AUTH,
    }),
    current: defineRoute<undefined, undefined, undefined, GetCurrentSubscriptionResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/subscription/current',
    }),
    usage: defineRoute<undefined, undefined, undefined, GetSubscriptionUsageResponse>({
      method: ApiCallMethod.Get,
      routeTemplate: '/api/subscription/usage',
    }),
    checkout: defineRoute<undefined, undefined, CreateSubscriptionCheckoutRequest, CreateSubscriptionCheckoutResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/subscription/checkout',
    }),
    changePlan: defineRoute<undefined, undefined, ChangeSubscriptionPlanRequest, void>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/subscription/change-plan',
    }),
    cancel: defineRoute<undefined, undefined, undefined, CancelSubscriptionResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/subscription/cancel',
    }),
    resume: defineRoute<undefined, undefined, undefined, ResumeSubscriptionResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/subscription/resume',
    }),
    portal: defineRoute<undefined, undefined, undefined, CreateSubscriptionPortalResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/subscription/portal',
    }),
  },
  system: {
    reachOut: defineRoute<undefined, undefined, ReachOutRequest, ReachOutResponse>({
      method: ApiCallMethod.Post,
      routeTemplate: '/api/reach-out',
      auth: PUBLIC_ROUTE_AUTH,
    }),
  },
} as const;

/**
 * Routes the BL server serves that `ROUTE_CONTRACTS` does not model.
 *
 * `ROUTE_CONTRACTS` describes the typed client surface — every route the front
 * end reaches through a generated api-client function. It was never the whole
 * HTTP surface: infrastructure probes, the cron tier, the published-string
 * resolvers, the admin error browser, and the one route the front end calls
 * with a raw `fetch` all live outside it. Boot-time reconciliation needs the
 * whole surface or it reports every one of those as a hole, so they are
 * declared here — same `visibility` vocabulary, plus the reason they are not
 * client routes.
 *
 * This is a declaration, not a permission: an entry here still has to match
 * what the server actually registers, and `reason` is what a reviewer reads
 * before believing a public one.
 *
 * The `/docs` subtree is absent on purpose — it belongs to `@fastify/swagger-ui`,
 * which registers it before the reconciliation hook is installed, so the check
 * never sees it and BL never has to track a vendor's internal route list.
 */
export interface ServerOnlyRoute {
  readonly method: ApiCallMethod;
  readonly routeTemplate: string;
  readonly auth: ApiRouteAuthConfig;
  readonly reason: string;
}

export const SERVER_ONLY_ROUTES: readonly ServerOnlyRoute[] = [
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/',
    auth: PUBLIC_ROUTE_AUTH,
    reason: 'Redirect to /docs. No auth check runs on it, so it answers anonymously.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/openapi.json',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'The generated OpenAPI document. No auth check runs on it, so it answers '
      + 'anonymously — the same posture as the /docs UI it feeds.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/health/live',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'Process liveness probe. A platform health check presents no credential, '
      + 'so requiring one would fail every deploy.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/health/ready',
    auth: PUBLIC_ROUTE_AUTH,
    reason: 'DB readiness probe, unauthenticated for the same reason as /health/live.',
  },
  {
    method: ApiCallMethod.Post,
    routeTemplate: '/cron/sweep-uploads',
    auth: PRIVATE_ROUTE_AUTH,
    reason:
      'Cron tier, not user tier: gated by the requireCronToken preHandler against '
      + 'a shared X-Cron-Token, so it takes a credential — just not a user bearer '
      + 'token, which is why no typed client function exists for it.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/l/:host/:code',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'Short-link click path. The address is a published string — quoted into '
      + 'deliverables, pasted into messages, used as an <img src> — so it cannot '
      + 'sit behind a login. Nothing here mints: a code exists only because a '
      + 'research tool was handed that URL by a vendor.',
  },
  {
    method: ApiCallMethod.Post,
    routeTemplate: '/l/resolve',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'Bulk short-link resolution, same plugin and same published-string reasoning '
      + 'as the click path.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/api/l/:host/:code',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'The short-link plugin mounted a second time under /api, because /api is the '
      + 'one prefix every environment already routes to BL. Same handlers, same '
      + 'posture as the root mount.',
  },
  {
    method: ApiCallMethod.Post,
    routeTemplate: '/api/l/resolve',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'The mount the front end actually calls, from useResolvedShortLink with a raw '
      + 'fetch rather than a generated client function — which is why it is declared '
      + 'here and not in ROUTE_CONTRACTS.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/r/*',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      'The research-address explainer page. It looks nothing up and answers the same '
      + 'static HTML for every path; whoever opens a published research address '
      + 'deserves the explanation rather than a login wall (ruled 2026-08-16).',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/media/exercise-video/:idOrSlug',
    auth: PUBLIC_ROUTE_AUTH,
    reason:
      "Redirects to a live pre-signed vendor video URL, because the vendor's own "
      + 'URLs expire 48h after issue while the published address must keep working. '
      + 'The slugs behind it are public; a per-IP caller gate, not authentication, '
      + 'is what protects the metered vendor call.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/api/system-errors',
    auth: PRIVATE_ROUTE_AUTH,
    reason:
      'Admin error browser — authenticated AND admin-only (authorizeAdmin). Not '
      + 'reached by the product front end, so it has no typed client function.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/api/system-errors/correlation/:correlationId',
    auth: PRIVATE_ROUTE_AUTH,
    reason: 'Admin error browser, same authorizeAdmin gate as the list route.',
  },
  {
    method: ApiCallMethod.Get,
    routeTemplate: '/api/system-errors/:id',
    auth: PRIVATE_ROUTE_AUTH,
    reason: 'Admin error browser, same authorizeAdmin gate as the list route.',
  },
];

export interface DeclaredRoute {
  /** `ROUTE_CONTRACTS` dotted path, or `METHOD /path` for a server-only route. */
  readonly key: string;
  readonly method: ApiCallMethod;
  readonly routeTemplate: string;
  readonly visibility: ApiRouteVisibility;
}

export interface CollectDeclaredRoutesInput {
  /** `false` yields the typed client surface alone — what the front end sees. */
  readonly includeServerOnly: boolean;
}

interface RouteContractShape {
  readonly method: ApiCallMethod;
  readonly routeTemplate: string;
  readonly auth?: ApiRouteAuthConfig;
}

function isRouteContractShape(value: unknown): value is RouteContractShape {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RouteContractShape>;
  return typeof candidate.method === 'string' && typeof candidate.routeTemplate === 'string';
}

function collectContractRoutes(input: { keyPrefix: string; value: unknown }): DeclaredRoute[] {
  if (isRouteContractShape(input.value)) {
    return [{
      key: input.keyPrefix,
      method: input.value.method,
      routeTemplate: input.value.routeTemplate,
      visibility: input.value.auth?.visibility ?? ApiRouteVisibility.Private,
    }];
  }

  if (!input.value || typeof input.value !== 'object') return [];

  return Object.entries(input.value as Record<string, unknown>).flatMap(([key, value]) =>
    collectContractRoutes({
      keyPrefix: input.keyPrefix ? `${input.keyPrefix}.${key}` : key,
      value,
    }),
  );
}

/**
 * Flattens the declared route surface. The single reader of `ROUTE_CONTRACTS`'
 * nesting — the boot-time reconciliation and the public-allowlist test both go
 * through here so neither re-implements the walk.
 */
export function collectDeclaredRoutes(input: CollectDeclaredRoutesInput): DeclaredRoute[] {
  const contractRoutes = collectContractRoutes({ keyPrefix: '', value: ROUTE_CONTRACTS });
  if (!input.includeServerOnly) return contractRoutes;

  const serverOnlyRoutes = SERVER_ONLY_ROUTES.map((route): DeclaredRoute => ({
    key: `${route.method} ${route.routeTemplate}`,
    method: route.method,
    routeTemplate: route.routeTemplate,
    visibility: route.auth.visibility ?? ApiRouteVisibility.Private,
  }));

  return [...contractRoutes, ...serverOnlyRoutes];
}
